import { useState } from "react";

import useAxios from "@hooks/use-axios";
import { confirmAlert, toastify } from "@helpers/toast";
import { OPS_ENDPOINTS } from "@constants/endpoints";
import { RETRY_COPY, SEND_COPY } from "../misc/constants";

const payloadOf = (res) => res?.data ?? res;

/**
 * Sends the brand's contract to publishers — every one not yet sent this
 * version, or a single one — and re-queues anything stuck. The API only
 * queues the work, so the list catches up afterwards rather than flipping
 * instantly; `onDone` is what makes it re-read.
 * @param brandId
 * @param onDone
 */
const useSendContracts = (brandId, onDone) => {
    const { axiosInstance, loading } = useAxios({ base: "ops", showAlert: false });
    const { axiosInstance: retryInstance, loading: retrying } = useAxios({ base: "ops", showAlert: false });
    // The publisher a single send is for, so only that row spins.
    const [sendingId, setSendingId] = useState(null);

    const send = async (body, publisherId = null) => {
        setSendingId(publisherId);

        try {
            const data = payloadOf(await axiosInstance.post(OPS_ENDPOINTS.CONTRACT_SEND(brandId), body));
            toastify({ title: SEND_COPY.result(data || {}), type: data?.failed ? "warning" : "success" });
        } catch (error) {
            toastify({ title: error?.response?.data?.message || SEND_COPY.failure, type: "error" });
        } finally {
            setSendingId(null);
            onDone?.();
        }
    };

    const handleSendAll = () => confirmAlert({
        title            : SEND_COPY.allConfirmTitle,
        text             : SEND_COPY.allConfirmText,
        confirmButtonText: SEND_COPY.confirmButton,
        preConfirm       : () => send({})
    });

    const handleSendOne = (row) => confirmAlert({
        title            : SEND_COPY.oneConfirmTitle(row.name),
        text             : SEND_COPY.oneConfirmText,
        confirmButtonText: SEND_COPY.confirmButton,
        preConfirm       : () => send({ holder_access_ids: [row.publisherId] }, row.publisherId)
    });

    const handleRetry = async () => {
        try {
            const data = payloadOf(await retryInstance.post(OPS_ENDPOINTS.CONTRACT_RETRY(brandId)));
            const retried = (data?.retried_sends || 0) + (data?.retried_brand_signs || 0);
            toastify({ title: RETRY_COPY.result(data || {}), type: data?.failed || !retried ? "warning" : "success" });
        } catch (error) {
            toastify({ title: error?.response?.data?.message || RETRY_COPY.failure, type: "error" });
        } finally {
            onDone?.();
        }
    };

    return {
        sendingAll: loading && !sendingId,
        isSending : (row) => loading && sendingId === row.publisherId,
        retrying,
        handleSendAll,
        handleSendOne,
        handleRetry
    };
};

export default useSendContracts;
